import React, { useEffect } from 'react'
import styled from 'styled-components'
import Layout from 'components/layout'
import { Helmet } from 'react-helmet'
import Content from 'components/new/content'

const IndependenceDayPartsPage = () => {
  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://formui-usa1.mkt.dynamics.com/usa/FormLoader/FormLoader.bundle.js'
    script.async = true
    document.body.appendChild(script)

    return () => {
      document.body.removeChild(script)
    }
  }, [])

  return (
    <Layout kind='white'>
      <Helmet>
        <title>Independence Day Parts Savings | Hutson Inc</title>
        <meta
          name='description'
          content='Celebrate Independence Day with exclusive savings on John Deere parts. Available June 29 – July 2 at all Hutson locations.'
        />
        <meta name='robots' content='noindex' />
      </Helmet>

      <Hero>
        <HeroOverlay />
        <HeroInner>
          <HeroTag>June 29 – July 2</HeroTag>
          <HeroTitle>Exclusive Independence Day Parts Savings</HeroTitle>
          <HeroSubtitle>
            Celebrate the holiday weekend with an exclusive offer on John Deere parts — available at
            all Hutson locations. Fill out the form below and we'll send your savings directly to
            you.
          </HeroSubtitle>
          <HeroCTA href='#claim-offer'>Claim Your Offer</HeroCTA>
        </HeroInner>
      </Hero>

      <DetailsSection>
        <Content>
          <SectionTitle>How It Works</SectionTitle>
          <DetailsGrid>
            <DetailCard>
              <StepNumber>1</StepNumber>
              <DetailTitle>Fill Out the Form</DetailTitle>
              <DetailText>
                Enter your information below to receive your exclusive parts savings.
              </DetailText>
            </DetailCard>
            <DetailCard>
              <StepNumber>2</StepNumber>
              <DetailTitle>Get Your Offer</DetailTitle>
              <DetailText>
                We'll send your exclusive savings directly to your inbox.
              </DetailText>
            </DetailCard>
            <DetailCard>
              <StepNumber>3</StepNumber>
              <DetailTitle>Visit Your Local Hutson</DetailTitle>
              <DetailText>
                Bring your offer in and save on in-stock and stock-order John Deere parts.
              </DetailText>
            </DetailCard>
          </DetailsGrid>
        </Content>
      </DetailsSection>

      <FormSection id='claim-offer'>
        <Content>
          <FormGrid>
            <FormInfo>
              <SectionTitleLight>Claim Your Exclusive Offer</SectionTitleLight>
              <FormDescription>
                Fill out the form and we'll send your exclusive John Deere parts savings straight to
                your inbox. Offer valid June 29 – July 2, 2026.
              </FormDescription>
              <BenefitsList>
                <BenefitItem>
                  <CheckMark />
                  <span>In-stock and stock-order John Deere parts</span>
                </BenefitItem>
                <BenefitItem>
                  <CheckMark />
                  <span>Exclusive savings sent directly to you</span>
                </BenefitItem>
                <BenefitItem>
                  <CheckMark />
                  <span>Available at all Hutson locations</span>
                </BenefitItem>
                <BenefitItem>
                  <CheckMark />
                  <span>Limited time: June 29 – July 2</span>
                </BenefitItem>
              </BenefitsList>
            </FormInfo>
            <FormContainer>
              <FormWrapper
                data-form-id='0409e91b-a365-f111-a825-000d3a1a2513'
                data-form-api-url='https://public-usa.mkt.dynamics.com/api/v1.0/orgs/216fb217-af17-ef11-9f85-000d3a130219/landingpageforms'
                data-cached-form-url='https://assets1-usa.mkt.dynamics.com/216fb217-af17-ef11-9f85-000d3a130219/digitalassets/forms/0409e91b-a365-f111-a825-000d3a1a2513'
              ></FormWrapper>
            </FormContainer>
          </FormGrid>
        </Content>
      </FormSection>

      <Disclaimer>
        <Content>
          <DisclaimerText>
            Offer valid June 29 – July 2, 2026, on in-stock and stock-order John Deere parts only.
            One offer per customer. See your local Hutson dealership for details.
          </DisclaimerText>
        </Content>
      </Disclaimer>
    </Layout>
  )
}

const CheckMark = () => (
  <CheckIcon>
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3'>
      <path d='M5 13l4 4L19 7' />
    </svg>
  </CheckIcon>
)

const Hero = styled.div`
  background-image: url('/lp/independence-day-parts-hero.jpg');
  background-position: center;
  background-size: cover;
  min-height: 500px;
  position: relative;
`

const HeroOverlay = styled.div`
  background: linear-gradient(to right, rgba(10, 30, 66, 0.92) 0%, rgba(10, 30, 66, 0.7) 100%);
  inset: 0;
  position: absolute;
`

const HeroInner = styled.div`
  margin: 0 auto;
  max-width: 800px;
  padding: 80px 24px;
  position: relative;
  text-align: center;
  z-index: 1;

  @media (min-width: 768px) {
    padding: 120px 48px;
  }
`

const HeroTag = styled.span`
  background-color: #c41230;
  border-radius: 2px;
  color: #fff;
  display: inline-block;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 2px;
  margin-bottom: 20px;
  padding: 8px 20px;
  text-transform: uppercase;
`

const HeroTitle = styled.h1`
  color: #fff;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.15;
  margin: 0 0 20px;

  @media (min-width: 600px) {
    font-size: 44px;
  }

  @media (min-width: 900px) {
    font-size: 52px;
  }
`

const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 17px;
  line-height: 1.7;
  margin: 0 auto 32px;
  max-width: 600px;

  @media (min-width: 600px) {
    font-size: 19px;
  }
`

const HeroCTA = styled.a`
  background-color: #c41230;
  border: 2px solid #c41230;
  color: #fff;
  display: inline-block;
  font-size: 16px;
  font-weight: 700;
  padding: 14px 40px;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background-color: #a30f28;
    border-color: #a30f28;
  }
`

const DetailsSection = styled.section`
  background-color: #fff;
`

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 40px;
  text-align: center;

  @media (min-width: 600px) {
    font-size: 36px;
  }
`

const SectionTitleLight = styled(SectionTitle)`
  color: #fff;
  text-align: left;
`

const DetailsGrid = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

const DetailCard = styled.div`
  padding: 32px 24px;
  text-align: center;
`

const StepNumber = styled.div`
  align-items: center;
  background-color: ${props => props.theme.color.g400};
  border-radius: 50%;
  color: #fff;
  display: inline-flex;
  font-size: 20px;
  font-weight: 700;
  height: 48px;
  justify-content: center;
  margin-bottom: 16px;
  width: 48px;
`

const DetailTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px;
  text-transform: none;
`

const DetailText = styled.p`
  color: ${props => props.theme.color.n400};
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
`

const FormSection = styled.section`
  background-color: ${props => props.theme.color.g700};
`

const FormGrid = styled.div`
  display: grid;
  gap: 40px;
  grid-template-columns: 1fr;

  @media (min-width: 900px) {
    align-items: start;
    gap: 64px;
    grid-template-columns: 1fr 1fr;
  }
`

const FormInfo = styled.div`
  color: #fff;
`

const FormDescription = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 17px;
  line-height: 1.7;
  margin: 0 0 28px;
`

const BenefitsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const BenefitItem = styled.li`
  align-items: center;
  color: #fff;
  display: flex;
  font-size: 16px;
  margin: 0;
  padding: 10px 0;

  span {
    margin-left: 12px;
  }
`

const CheckIcon = styled.div`
  color: ${props => props.theme.color.y400};
  flex-shrink: 0;
  height: 24px;
  width: 24px;
`

const FormContainer = styled.div`
  background-color: #fff;
  border-radius: 4px;
  padding: 32px;

  @media (min-width: 600px) {
    padding: 40px;
  }
`

const FormWrapper = styled.div`
  max-width: 100% !important;
  overflow: hidden;

  div,
  form,
  iframe {
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }

  div[data-editorblocktype] {
    font-family: 'JD Sans', Arial, Helvetica, sans-serif !important;
  }

  label {
    color: ${props => props.theme.color.n700} !important;
    font-family: 'JD Sans', Arial, Helvetica, sans-serif !important;
    font-size: 14px !important;
    font-weight: 600 !important;
    margin-bottom: 4px !important;
  }

  input[type='text'],
  input[type='email'],
  input[type='tel'],
  input[type='number'],
  select,
  textarea {
    border: 2px solid ${props => props.theme.color.n40} !important;
    border-radius: 2px !important;
    box-sizing: border-box !important;
    font-family: 'JD Sans', Arial, Helvetica, sans-serif !important;
    font-size: 16px !important;
    padding: 10px 12px !important;
    transition: border-color 0.2s ease !important;
    width: 100% !important;

    &:focus {
      border-color: ${props => props.theme.color.g400} !important;
      outline: none !important;
    }
  }

  button[type='submit'] {
    background-color: ${props => props.theme.color.y400} !important;
    border: 2px solid ${props => props.theme.color.y400} !important;
    border-radius: 0 !important;
    color: ${props => props.theme.color.n900} !important;
    cursor: pointer !important;
    font-family: 'JD Sans', Arial, Helvetica, sans-serif !important;
    font-size: 16px !important;
    font-weight: 700 !important;
    padding: 14px 32px !important;
    transition: all 0.2s ease !important;
    width: 100% !important;

    &:hover {
      background-color: ${props => props.theme.color.y500} !important;
      border-color: ${props => props.theme.color.y500} !important;
    }
  }
`

const Disclaimer = styled.div`
  background-color: ${props => props.theme.color.n10};
  padding: 0;
`

const DisclaimerText = styled.p`
  color: ${props => props.theme.color.n200};
  font-size: 13px;
  line-height: 1.6;
  margin: 0;
  text-align: center;
`

export default IndependenceDayPartsPage
